import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const TIERS = ["free", "starter", "plus", "unlimited"];
const KINDS = { voice: "Listen & Mic (shared)", draw: "Draw it diagrams" };

function LimitRow({ kind, values, onChange, readOnly }) {
  return (
    <div className="grid grid-cols-[1.4fr_repeat(4,1fr)] gap-2 items-center" data-testid={`daily-limits-row-${kind}`}>
      <span className="text-sm text-neutral-300">{KINDS[kind]}</span>
      {TIERS.map((t) => (
        <Input key={t} type="number" min={0} max={1000} value={values[t] ?? 0} onChange={(e) => onChange(kind, t, e.target.value)} disabled={readOnly}
          className="h-9 bg-[#0B1220] border-[#22304B]" aria-label={`${KINDS[kind]} for ${t}`} data-testid={`daily-limit-${kind}-${t}`} />
      ))}
    </div>
  );
}

export function DailyLimitsPanel({ readOnly }) {
  const { refreshEnt } = useAuth();
  const [limits, setLimits] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/admin/daily-limits").then((r) => setLimits(r.data.limits))
    .catch((e) => toast.error(`Couldn't load daily limits: ${formatError(e)}`)), []);
  useEffect(() => { load(); }, [load]);

  const change = (kind, tier, v) => setLimits((l) => ({ ...l, [kind]: { ...l[kind], [tier]: v === "" ? "" : Number(v) } }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/admin/daily-limits", limits);
      setLimits(data.limits);
      refreshEnt();
      toast.success("Daily limits saved");
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  if (!limits) return null;
  return (
    <div className="rounded-2xl border border-[#1B2740] bg-[#0E1626] p-5 space-y-3" data-testid="daily-limits-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Daily limits per student</p>
          <p className="text-xs text-neutral-500">Resets at midnight UTC. Set 0 to turn a feature off for a plan. Personal limits on a student's record override these.</p>
        </div>
        {readOnly ? <span className="text-xs text-neutral-500" data-testid="daily-limits-readonly">View only for your role</span> : (
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50" data-testid="daily-limits-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-[1.4fr_repeat(4,1fr)] gap-2 text-[11px] uppercase tracking-wider text-neutral-500">
        <span />{TIERS.map((t) => <span key={t}>{t}</span>)}
      </div>
      {Object.keys(KINDS).map((k) => <LimitRow key={k} kind={k} values={limits[k]} onChange={change} readOnly={readOnly} />)}
      <p className="text-[11px] text-neutral-600">Draw it is only available on Plus and Unlimited, whatever the limit.</p>
    </div>
  );
}
